import { commaize, pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatBytes } from '@kaiizen/kai-presenters';
import { useFileCache, useManageScanPhotosSync, usePhotoGallery } from '@kaiizen/kai-pro-api-react-state';
import { usePersistentCache } from '@kaiizen/react-persistent-cache';

import { useConfirmationModal, useToast } from '@/Client/State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button, LabeledProgressBar } from '@/Client/Widgets';

const MODAL_OVERLAY_STYLE = css`
  label: MODAL_OVERLAY_STYLE;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const MODAL_CONTENT_STYLE = css`
  label: MODAL_CONTENT_STYLE;
  background: var(--background);
  padding: ${LAYOUT2.spacing3}px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.15);
`;

const MODAL_TITLE_STYLE = css`
  label: MODAL_TITLE_STYLE;
  ${TYPOGRAPHY2.largeBold}
  color: var(--text);
  text-align: center;
  margin-bottom: ${LAYOUT2.spacing3}px;
`;

const MODAL_STATS_STYLE = css`
  label: MODAL_STATS_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
  margin-bottom: ${LAYOUT2.spacing3}px;
`;

const MODAL_ADVANCED_STYLE = css`
  label: MODAL_ADVANCED_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing}px;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);

  padding-top: ${LAYOUT2.spacing2}px;
  padding-bottom: ${LAYOUT2.spacing2}px;
`;

const MODAL_ADVANCED_TITLE_STYLE = css`
  label: MODAL_ADVANCED_TITLE_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
  text-align: center;
`;

const MODAL_STAT_ROW_STYLE = css`
  label: MODAL_STAT_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${LAYOUT2.spacing1}px 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const MODAL_STAT_LABEL_STYLE = css`
  label: MODAL_STAT_LABEL_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text2);
`;

const MODAL_BUTTONS_STYLE = css`
  label: MODAL_BUTTONS_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacing2}px;
  justify-content: space-between;
`;

const MODAL_BUTTON_STYLE = css`
  label: MODAL_BUTTON_STYLE;
  flex: 1;
`;

interface CacheInfo {
  fileCacheSize: number;
  fileCacheFilesCount: number;
  fileCacheMaxSize: number;
  persistentCacheMemoryEntries: number;
  persistentCacheMemoryBytes: number;
  persistentCachePersistedEntries: number;
  persistentCachePersistedBytes: number;
  photoGalleryUnsyncedPhotos: number;
  photoGalleryUnsyncedBytes: number;
}

interface CacheInfoModalProps {
  visible: boolean;
  onClose: () => void;
}

export const CacheInfoModal = ({ visible, onClose }: CacheInfoModalProps) => {
  const persistentCache = usePersistentCache();
  const photoGallery = usePhotoGallery();
  const { manageScanPhotosSync } = useManageScanPhotosSync();
  const confirm = useConfirmationModal();
  const fileCache = useFileCache();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | undefined>(undefined);

  const { showToast } = useToast();

  const updateCacheInfo = useCallback(async () => {
    const fileCacheMaxSize = fileCache.getMaxCacheSize();
    const fileCacheInfo = await fileCache.getCacheInfo();
    const fileCacheSize = fileCacheInfo.cacheSize;
    const fileCacheFilesCount = fileCacheInfo.fileCount;
    const persistentCacheMemoryStats = await persistentCache.getInMemoryCacheStats();
    const persistentCachePersistedStats = await persistentCache.getPersistentCacheStats();

    const photoGalleryInfo = await photoGallery.getPhotoGalleryInfo();
    setCacheInfo({
      persistentCacheMemoryEntries: persistentCacheMemoryStats.count,
      persistentCacheMemoryBytes: persistentCacheMemoryStats.size,
      persistentCachePersistedEntries: persistentCachePersistedStats.count,
      persistentCachePersistedBytes: persistentCachePersistedStats.size,
      fileCacheSize,
      fileCacheFilesCount,
      fileCacheMaxSize,
      photoGalleryUnsyncedPhotos: photoGalleryInfo.unsyncedPhotos.numPhotos,
      photoGalleryUnsyncedBytes: photoGalleryInfo.unsyncedPhotos.numBytes,
    });
  }, [persistentCache, photoGallery, fileCache]);

  useEffect(() => {
    if (!visible) return;
    manageScanPhotosSync().then(() => updateCacheInfo());
  }, [visible, manageScanPhotosSync, updateCacheInfo]);

  useEffect(() => {
    if (!visible) return;
    updateCacheInfo();
  }, [visible, updateCacheInfo]);

  const clearAllCache = () => {
    persistentCache.purgeAllEntries();
    photoGallery.clearPhotoCache();
    showToast('Cache cleared successfully', 'success');
    updateCacheInfo();
  };

  const dangerouslyEraseUnsyncedPhotos = () => {
    confirm({
      title: 'Clear Unsynced Photos',
      message: 'Are you sure you want to clear all unsynced photos? This will delete all photos that have not been synced to the server.',
      confirmLabel: 'Clear',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        await photoGallery.dangerouslyEraseUnsyncedPhotos();
        updateCacheInfo();
        showToast('Unsynced photos cleared successfully', 'success');
      },
    });
  };

  const cacheEmpty = useMemo(() => !cacheInfo ? false : Object.values(cacheInfo).every(value => value === 0), [cacheInfo]);
  const haveUnsyncedPhotos = cacheInfo ? cacheInfo.photoGalleryUnsyncedPhotos > 0 : false;
  if (!visible) return null;

  return (
    <div css={MODAL_OVERLAY_STYLE} onClick={onClose}>
      <div css={MODAL_CONTENT_STYLE} onClick={event => event.stopPropagation()}>
        <h2 css={MODAL_TITLE_STYLE}>Cache Information</h2>

        <div css={MODAL_STATS_STYLE}>
          {cacheInfo
            ? (
              <>
                {haveUnsyncedPhotos && (
                  <LabeledProgressBar
                    label='Unsynced Photos'
                    valueLabel={pluralize(cacheInfo.photoGalleryUnsyncedPhotos, 'photo')}
                    value={cacheInfo.photoGalleryUnsyncedBytes}
                    max={cacheInfo.photoGalleryUnsyncedBytes}
                    maxLabel={formatBytes(cacheInfo.photoGalleryUnsyncedBytes)}
                    filledColor={COLORS2.namedColors.yellow}
                    emptyColor={COLORS2.namedColors.yellow}
                  />
                )}

                <LabeledProgressBar
                  label='Data Cache'
                  rightLabel={commaize(cacheInfo.persistentCacheMemoryEntries)
                    + ' / '
                    + pluralize(
                      cacheInfo.persistentCachePersistedEntries,
                      'entry',
                    )}
                  value={cacheInfo.persistentCacheMemoryBytes}
                  max={cacheInfo.persistentCachePersistedBytes}
                  valueLabel={formatBytes(cacheInfo.persistentCacheMemoryBytes)
                    + ' active'}
                  maxLabel={formatBytes(cacheInfo.persistentCachePersistedBytes)
                    + ' total'}
                  filledColor={COLORS2.namedColors.yellow}
                  emptyColor={COLORS2.namedColors.lightestGray}
                />
                <LabeledProgressBar
                  label='Photo Cache'
                  rightLabel={pluralize(cacheInfo.fileCacheFilesCount, 'photo')}
                  value={cacheInfo.fileCacheSize}
                  max={cacheInfo.fileCacheMaxSize}
                  valueLabel={formatBytes(cacheInfo.fileCacheSize) + ' used'}
                  maxLabel={formatBytes(cacheInfo.fileCacheMaxSize) + ' max'}
                  filledColor={COLORS2.namedColors.yellow}
                  emptyColor={COLORS2.namedColors.lightestGray}
                />
              </>
            )
            : (
              <div css={MODAL_STAT_ROW_STYLE}>
                <span css={MODAL_STAT_LABEL_STYLE}>Cache is loading...</span>
              </div>
            )}
          {showAdvanced && (
            <div css={MODAL_ADVANCED_STYLE}>
              <div css={MODAL_ADVANCED_TITLE_STYLE}>Advanced</div>
              {/* clear in-memory data cache, clear persisted data-cache, clear just photo-cache*/}
              <Button
                onClick={async () => {
                  await persistentCache.freeInMemoryEntries();
                  updateCacheInfo();
                }}
                css={MODAL_BUTTON_STYLE}
                buttonStyle='blue'
              >
                Reset Active Data
              </Button>
              <Button
                onClick={async () => {
                  await persistentCache.purgeAllEntries();
                  updateCacheInfo();
                }}
                css={MODAL_BUTTON_STYLE}
                buttonStyle='blue'
              >
                Clear Data Cache
              </Button>
              {haveUnsyncedPhotos && (
                <>
                  <Button
                    buttonStyle='yellow'
                    onClick={async () => {
                      await manageScanPhotosSync().then(results => {
                        showToast(`${pluralize(results.numResyncing, 'photo')} queued for sync`, 'success');
                        updateCacheInfo();
                      });
                    }}
                    css={MODAL_BUTTON_STYLE}
                  >
                    Force Sync Unsynced Photos
                  </Button>
                  <Button onClick={dangerouslyEraseUnsyncedPhotos} css={MODAL_BUTTON_STYLE} buttonStyle='red'>
                    Clear Unsynced Photos
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div css={MODAL_BUTTONS_STYLE}>
          <Button
            onClick={async () => {
              await photoGallery.clearPhotoCache();
              updateCacheInfo();
            }}
            css={MODAL_BUTTON_STYLE}
            buttonStyle='blue'
          >
            Clear Photo Cache
          </Button>
          <Button onClick={() => setShowAdvanced(!showAdvanced)} css={MODAL_BUTTON_STYLE} buttonStyle='white' disabled={cacheEmpty}>
            Advanced
          </Button>

          <Button onClick={onClose} css={MODAL_BUTTON_STYLE} buttonStyle='white'>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
